/**
 * Wire POJOs for form access rules, batch write envelopes, and {@code points/access.json}.
 * Mirrors Java: {@code IngestionPointAction}, {@code IngestionPointAccessRule*}, {@code ResultsPojo},
 * {@code ItemResultDoc}, {@code IngestionPointAccessRulesUpdateEntry}.
 */
export namespace IngestionAccess {
    /** Mirrors {@code com.collegenet.series25.document.ingestion.IngestionPointAction}. */
    export enum IngestionPointAction {
        FILL = "FILL",
        COPY = "COPY",
        EDIT_SCHEMA = "EDIT_SCHEMA",
        EDIT_WORKFLOW = "EDIT_WORKFLOW",
        EDIT_SETTINGS = "EDIT_SETTINGS",
        EDIT_PERMISSIONS = "EDIT_PERMISSIONS",
        VIEW_ALL_SUBMISSIONS = "VIEW_ALL_SUBMISSIONS",
        DELETE = "DELETE",
        DISTRIBUTE = "DISTRIBUTE",
    }

    /** Mirrors {@code IngestionPointAccessRuleObject} string constants. */
    export const AccessRuleObjectType = {
        CONT_ID: "cont_id",
        CONT_ROLE_ID: "cont_role_id",
        SEC_GROUP_ID: "sec_group_id",
        ACCOUNT_ID: "account_id",
    } as const;

    /**
     * Wire values for {@link IngestionPointAccessRule#principalKind} (API / Java).
     * Use instead of string literals in UI and clients.
     */
    export const RulePrincipal = {
        PUBLIC: "PUBLIC",
        LOGGED_IN: "LOGGED_IN",
        CONTACTS: "CONTACTS",
        SECURITY_GROUPS: "SECURITY_GROUPS",
        ORG_ROLE: "ORG_ROLE",
        ORG_RIGHT: "ORG_RIGHT",
    } as const;

    export type RulePrincipalValue = (typeof RulePrincipal)[keyof typeof RulePrincipal];

    /** Wire values for {@link IngestionPointAccessRule#scopeKind}. */
    export const RuleScope = {
        SUB_CAT: "SUB_CAT",
        ORGS: "ORGS",
        ORG_QUERY: "ORG_QUERY",
    } as const;

    export type RuleScopeValue = (typeof RuleScope)[keyof typeof RuleScope];

    /** Minimum organization-right access level (N = None through F = Full). */
    export const RuleOrgRightLevel = {
        NONE: "N",
        READ: "R",
        CREATE_UPDATE: "C",
        FULL: "F",
    } as const;

    export type RuleOrgRightLevelValue = (typeof RuleOrgRightLevel)[keyof typeof RuleOrgRightLevel];

    /** Display / save order for org-right minimum level selector. */
    export const ORG_RIGHT_MIN_LEVELS: readonly RuleOrgRightLevelValue[] = [
        RuleOrgRightLevel.NONE,
        RuleOrgRightLevel.READ,
        RuleOrgRightLevel.CREATE_UPDATE,
        RuleOrgRightLevel.FULL,
    ];

    /** Default min level when creating an org-right rule (Read). */
    export const DEFAULT_ORG_RIGHT_MIN_LEVEL = RuleOrgRightLevel.READ;

    /** Mirrors {@code com.collegenet.series25.document.resultsPojo.ErrorDoc}. */
    export interface ErrorDoc {
        msg?: string;
        msgId?: string;
        procError?: string;
    }

    /** Same wire shape as {@link ErrorDoc}; mirrors {@code InfoDoc}. */
    export type InfoDoc = ErrorDoc;

    /**
     * Mirrors {@code com.collegenet.series25.document.resultsPojo.ResultsPojo} — {@code results} envelope on batch rows.
     */
    export interface ResultsPojo {
        engine?: string;
        pubdate?: string;
        info?: InfoDoc;
        error?: ErrorDoc;
    }

    /** Mirrors {@code com.collegenet.series25.document.resultsPojo.ValidationErrorDoc}. */
    export interface ValidationErrorDoc {
        field: string;
        message: string;
    }

    /** Mirrors {@code com.collegenet.series25.document.ingestion.IngestionPointAccessRuleAction}. */
    export interface IngestionPointAccessRuleAction {
        accessRuleId?: number;
        formAction?: string;
    }

    /** Mirrors {@code com.collegenet.series25.document.ingestion.IngestionPointAccessRuleObject}. */
    export interface IngestionPointAccessRuleObject {
        accessRuleId?: number;
        objectType?: string;
        objectId?: number;
        objectName?: string;
    }

    /** Mirrors {@code com.collegenet.series25.document.ingestion.IngestionPointAccessRuleRight}. */
    export interface IngestionPointAccessRuleRight {
        accessRuleId?: number;
        rightName?: string;
        minAccessLevel?: string;
    }

    /** Mirrors {@code com.collegenet.series25.document.ingestion.IngestionPointAccessRule}. */
    export interface IngestionPointAccessRule {
        accessRuleId?: number;
        ingestionPointId?: number;
        instance?: string;
        sortOrder?: number;
        ruleName?: string;
        active?: boolean;
        principalKind?: string;
        scopeKind?: string;
        orgQueryId?: number;
        actions?: IngestionPointAccessRuleAction[];
        objects?: IngestionPointAccessRuleObject[];
        orgRight?: IngestionPointAccessRuleRight;
    }

    /** One row for GET/PUT {@code points/access.json}; mirrors {@code IngestionPointAccessRulesUpdateEntry}. */
    export interface IngestionPointAccessRulesUpdateEntry {
        ingestionPointId?: number;
        accessRules?: IngestionPointAccessRule[];
    }

    /**
     * PUT body root for {@code points/access.json}: MVC Jackson {@code UNWRAP_ROOT_VALUE} expects property {@code "List"}
     * for {@code List<IngestionPointAccessRulesUpdateEntry>}.
     */
    export interface IngestionPointAccessRulesUpdateEntryListRoot {
        List: IngestionPointAccessRulesUpdateEntry[];
    }

    /** Mirrors {@code IngestionPointAccessDecision.TraceEntry} JSON. */
    export interface IngestionPointAccessTraceEntry {
        phase: string;
        detail: string;
        granted: boolean;
    }

    /** Mirrors {@code IngestionPointAccessDecision} for batch FORBIDDEN items. */
    export interface IngestionPointAccessDecision {
        allowed: boolean;
        denyReason?: string | null;
        trace: IngestionPointAccessTraceEntry[];
        orgRightAccessDecision?: unknown;
    }

    /**
     * Batch API response: JSON array root {@code ItemResultDoc<T>[]} (same index as request).
     * Mirrors {@code List<ItemResultDoc<T>>} on the server.
     */
    export type ItemResultDocArray<T> = ItemResultDoc<T>[];

    /**
     * One batch row; mirrors {@code com.collegenet.series25.document.ingestion.ItemResultDoc}.
     * Each variant includes {@link ResultsPojo} on {@code results}.
     */
    export type ItemResultDoc<T> =
        | { status: "OK"; item: T; results: ResultsPojo }
        | { status: "FORBIDDEN"; accessDecision: IngestionPointAccessDecision; results: ResultsPojo }
        | { status: "NOT_FOUND"; id?: number; results: ResultsPojo }
        | { status: "VALIDATION_FAILED"; validationErrors: ValidationErrorDoc[]; results: ResultsPojo }
        | { status: "CONFLICT"; results: ResultsPojo }
        | { status: "INTERNAL_ERROR"; results: ResultsPojo };
}
